import { GridColDef, GridValueGetterParams } from "@mui/x-data-grid-premium";
import dayjs from "dayjs";

import { formatDateLocale } from "@/shared/utils/formatDate";
import { formatCurrencyValue, formatUnitValue } from "@/Utils/formatters";
import { MetricType } from "@/Utils/sharedTypes";

import { GroupedSalesCompositionRow } from "./api/getSalesCompositionV2";

// Mapping for field name transformations
const FIELD_NAME_MAPPING: Record<string, string> = {
  error_sale: "Unattributed Sale",
};

// Get display name for a field
const getDisplayName = (fieldName: string): string => {
  const mappedName = FIELD_NAME_MAPPING[fieldName];
  if (mappedName) {
    return mappedName;
  }

  // Convert snake_case to Title Case
  return fieldName
    .split("_")
    .filter((word) => word.length > 0)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Format product hierarchy like in other components
const formatProductHierarchy = (brand: string, category: string, product: string): string => {
  const parts = [brand, category, product].filter((part) => part && part !== "default");
  return parts.join(" > ");
};

// Get all unique variables across all factors and rows
export const getAllVariables = (data: GroupedSalesCompositionRow[]) => {
  const variables = new Set<string>();

  data.forEach((row) => {
    Object.values(row.sales || {}).forEach((factorData) => {
      Object.keys(factorData || {}).forEach((variable) => {
        variables.add(variable);
      });
    });
  });

  return Array.from(variables);
};

// Generate all active columns based on available variables and metric type
export const getAllActiveColumns = (
  data: GroupedSalesCompositionRow[],
  metricType: MetricType,
  geoViewToggle?: string,
  businessUnitToggle?: string
): string[] => {
  const variables = getAllVariables(data);

  const activeColumns = ["period_start"];

  // Add geography column if split by geography
  if (geoViewToggle === "split") {
    activeColumns.push("geography");
  }

  // Add business unit column if split by business unit
  if (businessUnitToggle === "split") {
    activeColumns.push("business_unit");
    activeColumns.push("product_hierarchy");
  }

  variables.forEach((variable) => {
    if (metricType === MetricType.SALES_REVENUE) {
      activeColumns.push(`${variable}_sales`);
    } else {
      activeColumns.push(`${variable}_units`);
    }
  });

  return activeColumns;
};

// Get variable value from nested factor structure
const getVariableValue = (
  row: GroupedSalesCompositionRow,
  variable: string,
  dataType: "sales" | "units"
): number => {
  const dataSource = dataType === "sales" ? row.sales : row.units;

  // Search through all factors to find the variable
  for (const factorData of Object.values(dataSource || {})) {
    if (factorData && factorData[variable] !== undefined) {
      return factorData[variable] || 0;
    }
  }

  return 0;
};

export const getSalesDriversColumnDefinitions = (
  data: GroupedSalesCompositionRow[],
  activeColumns: string[],
  options: {
    currencySymbol: string;
    formatCurrency: (val: number) => string;
    metricType: MetricType;
    geoViewToggle?: string;
    businessUnitToggle?: string;
  }
): GridColDef<GroupedSalesCompositionRow>[] => {
  const { formatCurrency, currencySymbol, metricType, geoViewToggle, businessUnitToggle } = options;

  const variables = getAllVariables(data);

  const columns: GridColDef<GroupedSalesCompositionRow>[] = [
    {
      field: "period_start",
      headerName: "Date",
      valueGetter: ({ row }: GridValueGetterParams<GroupedSalesCompositionRow>) =>
        formatDateLocale(dayjs(row.period_start), { yearFormat: "YY" }),
      sortComparator: (v1, v2, param1, param2) => {
        // Sort by the actual date values, not the formatted strings
        const date1 = new Date(param1.api.getRow(param1.id)?.period_start);
        const date2 = new Date(param2.api.getRow(param2.id)?.period_start);
        return date1.getTime() - date2.getTime();
      },
      sortable: true,
      hideable: !activeColumns.includes("period_start"),
      type: "string",
      flex: 1,
    },
  ];

  // Add geography column if split by geography
  if (geoViewToggle === "split") {
    columns.push({
      field: "geography",
      headerName: "Geography",
      valueGetter: ({ row }: GridValueGetterParams<GroupedSalesCompositionRow>) =>
        row.geography || "",
      sortable: true,
      hideable: !activeColumns.includes("geography"),
      type: "string",
      flex: 1,
    });
  }

  // Add business unit columns if split by business unit
  if (businessUnitToggle === "split") {
    columns.push({
      field: "product_hierarchy",
      headerName: "Products",
      valueGetter: ({ row }: GridValueGetterParams<GroupedSalesCompositionRow>) => {
        return formatProductHierarchy(row.brand || "", row.category || "", row.product || "");
      },
      sortable: true,
      hideable: !activeColumns.includes("product_hierarchy"),
      type: "string",
      flex: 1,
    });
  }

  // Define variable order - other_factors first, then others
  const priorityVariables = ["base_sale", "error_sale", "price_sale"];

  // Order variables: priority first, then alphabetically
  const orderedVariables = [
    ...priorityVariables.filter((variable) => variables.includes(variable)),
    ...variables.filter((variable) => !priorityVariables.includes(variable)).sort(),
  ];

  orderedVariables.forEach((variable) => {
    const displayName = getDisplayName(variable);

    if (metricType === MetricType.SALES_REVENUE) {
      const fieldName = `${variable}_sales`;
      columns.push({
        field: fieldName,
        headerName: `${displayName} (${currencySymbol})`,
        valueGetter: ({ row }: GridValueGetterParams<GroupedSalesCompositionRow>) => {
          return getVariableValue(row, variable, "sales");
        },
        valueFormatter: ({ value }: { value: number }) =>
          formatCurrencyValue(value, formatCurrency),
        sortable: true,
        type: "number",
        hideable: !activeColumns.includes(fieldName),
        flex: 1,
      });
    } else {
      // Units
      const fieldName = `${variable}_units`;
      columns.push({
        field: fieldName,
        headerName: `${displayName} (Units)`,
        valueGetter: ({ row }: GridValueGetterParams<GroupedSalesCompositionRow>) => {
          return getVariableValue(row, variable, "units");
        },
        valueFormatter: ({ value }: { value: number }) => formatUnitValue(value),
        sortable: true,
        type: "number",
        hideable: !activeColumns.includes(fieldName),
        flex: 1,
      });
    }
  });
  return columns;
};
